import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { toast } from 'react-toastify';
import SkeletonComponent from '../SkeletonComponent';
import { AvlTable } from './Table/Table';
import { getPurchasesOfFolder, getFolderTitleNumbers } from '../../Services/Search.services';
import { datetimeToFormattedString } from '../../Utils/Utils';

const tableHeaders = [
    { label: 'Ordered At', align: 'left' },
    { label: 'Document ID', align: 'left' },
    { label: 'Purchase Type', align: 'left' },
    { label: 'Title Reference', align: 'left' },
    { label: 'User', align: 'left' },
    { label: 'Fee', align: 'left' },
    { label: 'Status', align: 'left' },
    { label: 'Is Deleted', align: 'center' },
];

const purchaseTypes = [
    { value: 'digital-mapping', label: 'Digital Mapping' },
    { value: 'digital-title-plan', label: 'Digital Title Plan' },
    { value: 'linked-documents', label: 'Linked Documents' },
    { value: 'linked-documents-analysis', label: 'Linked Documents Analysis' },
    { value: 'planning', label: 'Planning' },
    { value: 'scottish-app-enquiry', label: 'Scottish App Enquiry' },
    { value: 'scottish-title', label: 'Scottish Title' },
    { value: 'scottish-title-plan', label: 'Scottish Title Plan' },
    { value: 'title-plan', label: 'Title Plan' },
    { value: 'title-register', label: 'Title Register' },
];

const statusOptions = [
    { value: 'in progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'error', label: 'Error' },
];

const PurchasesModal = ({ folderId }) => {
    const [isLoading, setIsLoading] = useState(true);

    const [records, setRecords] = useState([]);
    const [paginatedRecords, setPaginatedRecords] = useState([]);
    const [titleNumbers, setTitleNumbers] = useState([]);

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(1);
    const [totalRows, setTotalRows] = useState(0);

    const [purchaseType, setPurchaseType] = useState('');
    const [purchaseStatus, setPurchaseStatus] = useState('');
    const [searchText, setSearchText] = useState('');

    const filteredPurchases = useMemo(() => {
        return records.filter((item) => {
            if (purchaseType && item.kind !== purchaseType) return false;

            if (purchaseStatus) {
                if (purchaseStatus === 'error' && !item.is_error) return false;
                if (purchaseStatus === 'in progress' && item.status !== 'in progress') return false;
                if (purchaseStatus === 'completed' && item.status !== 'completed') return false;
            }

            if (searchText) {
                return item.reference === searchText;
            }

            return true;
        });
    }, [records, purchaseType, purchaseStatus, searchText]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        const offset = (page - 1) * rowsPerPage;
        const paginated = filteredPurchases.slice(offset, offset + rowsPerPage);

        setPaginatedRecords(paginated);
        setTotalRows(filteredPurchases.length);
        setPageCount(Math.ceil(filteredPurchases.length / rowsPerPage));
    }, [filteredPurchases, page, rowsPerPage]);

    const handlePageChange = (_, value) => setPage(value);

    const handleChangeRowsPerPage = (rows) => {
        setRowsPerPage(rows);
        setPage(1);
    };

    const loadData = async () => {
        setIsLoading(true);

        try {
            const [purchasesResponse, titleNumbersResponse] = await Promise.all([
                getPurchasesOfFolder(folderId),
                getFolderTitleNumbers(folderId)
            ]);

            const purchases = purchasesResponse?.purchases || [];
            const titles = titleNumbersResponse?.title_numbers || [];

            const sorted = [...purchases].sort(
                (a, b) => new Date(b.performed_at) - new Date(a.performed_at)
            );
            setRecords(sorted);
            setTitleNumbers(titles);
        } catch (error) {
            setRecords([]);
            setTitleNumbers([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyDocumentId = (documentId) => {
        navigator.clipboard.writeText(documentId);
        toast.success('Document ID copied to clipboard');
    };

    const getStatusStyle = (row) => {
        if (row.is_error) return { color: 'red', fontWeight: 700 };

        return {};
    };

    const convertToCells = (row) => [
        {
            data: row.performed_at 
                ? datetimeToFormattedString(row.performed_at)
                : '-',
            style: { minWidth: '140px' },
        },
        {
            data: row.document_id ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{row.document_id}</span>
                    <Tooltip title='Copy Document ID'>
                        <IconButton
                            size='small'
                            onClick={() => handleCopyDocumentId(row.document_id)}
                            sx={{ 
                                p: 0.5,
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                            }}
                        >
                            <ContentCopyIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : '-',
        },
        { 
            data: purchaseTypes.find((pt) => pt.value === row.kind)?.label || row.kind || '-' 
        },
        { data: row.reference || '-' },
        { data: row.user_name || '-' },
        { data: row.fee ?? '-' },
        {
            data: row.is_error && row.message ? (
                <Tooltip title={row.message}>
                    <span style={getStatusStyle(row)}>{row.status || '-'}</span>
                </Tooltip>
            ) : (
                <span style={getStatusStyle(row)}>{row.status || '-'}</span>
            ),
        },
        {
            data: row.is_deleted !== undefined ? row.is_deleted.toString() : '-',
            align: 'center',
        },
    ];

    const tableRows = paginatedRecords.map((row) => ({
        cells: convertToCells(row),
    }));

    return (
        <div
            style={{
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    select
                    size='small'
                    label='Purchase Type'
                    value={purchaseType}
                    onChange={(e) => {
                        setPurchaseType(e.target.value);
                        setPage(1);
                    }}
                    sx={{ minWidth: 200 }}
                    SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                    InputLabelProps={{ shrink: true }}
                >
                    <MenuItem value=''>All</MenuItem>
                    {purchaseTypes.map((type) => (
                        <MenuItem 
                            key={type.value} 
                            value={type.value}
                        >
                            {type.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    size='small'
                    label='Status'
                    value={purchaseStatus}
                    onChange={(e) => {
                        setPurchaseStatus(e.target.value);
                        setPage(1);
                    }}
                    sx={{ minWidth: 200 }}
                    SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                    InputLabelProps={{ shrink: true }}
                >
                    <MenuItem value=''>All</MenuItem>
                    {statusOptions.map((status) => (
                        <MenuItem 
                            key={status.value} 
                            value={status.value}
                        >
                            {status.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    size='small'
                    label='Title Number'
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                        setPage(1);
                    }}
                    sx={{ minWidth: 200 }}
                    SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                    InputLabelProps={{ shrink: true }}
                >
                    <MenuItem value=''>All</MenuItem>
                    {titleNumbers.map((number) => (
                        <MenuItem 
                            key={number} 
                            value={number}
                        >
                            {number}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {isLoading ? (
                <SkeletonComponent />
            ) : paginatedRecords.length ? (
                <AvlTable
                    headers={tableHeaders}
                    rows={tableRows}
                    page={page}
                    pageCount={pageCount}
                    rowsPerPage={rowsPerPage}
                    rowsCount={totalRows}
                    isRowsPerPageOptionsVisible
                    onPageChange={handlePageChange}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            ) : (
                <Stack sx={{ width: '100% !important' }} spacing={2}>
                    <Alert severity="warning">
                        {searchText || purchaseType || purchaseStatus
                            ? 'No purchases match your filters'
                            : 'No purchases found for this folder'}
                    </Alert>
                </Stack>
            )}
        </div>
    );
};

PurchasesModal.propTypes = {
    folderId: PropTypes.string.isRequired,
};

export default PurchasesModal;
